/**
 * Platform-only creator cell (V2 alpha).
 *
 * Every creators list inside the campaign-detail page renders three things
 * per row: the creator's platform avatar, their platform display name
 * (e.g. their channel name) on top, and their `@username` as a clickable
 * link to their profile on the campaign's platform.
 *
 * The campaign itself is single-platform, so we do NOT label each row
 * with "YOUTUBE" / "INSTAGRAM" — that's redundant noise when every row
 * in the tab belongs to the same platform.
 *
 * No email, no real-name field — those exist on the creator record but
 * aren't carried by the lightweight roster rows the campaign tabs iterate
 * over, and we don't want the lists to drift into "CRM card" territory.
 *
 * Canonical creator shape consumed:
 *   {
 *     "platform": {
 *       "username": "CuttingEdgeSchool",
 *       "name": "The Cutting Edge School",
 *       "profile_picture_url": "https://...",
 *       "is_verified": false
 *     }
 *   }
 */
import * as React from 'react';
import type { Platform } from '@discovr/types';
import { Tooltip, TooltipTrigger, TooltipContent } from '../components/ui/tooltip';
import { useIsTruncated } from './useIsTruncated';

export type CampaignPlatform = Platform | string | undefined;

/** The creator fields the cell reads. Report rows, roster entries and the
 *  looser legacy shapes all satisfy it. */
export interface PlatformCreatorSource {
  platform?: {
    username?: string;
    name?: string;
    profile_picture_url?: string;
    is_verified?: boolean;
  };
  creator?: { name?: string };
  name?: string;
  handle?: string;
  profile_pic?: string;
}

const PLATFORM_PROFILE_URL: Partial<Record<string, (username: string) => string>> = {
  youtube: (u) => `https://www.youtube.com/@${u}`,
  instagram: (u) => `https://www.instagram.com/${u}`,
};

/**
 * Resolve the (display name, username, profile URL, profile picture)
 * tuple for a creator under a given campaign platform. Pure function — no
 * React, safe in tests.
 *
 * Display name comes from `creator.platform.name` (the channel / profile
 * display name), falling back to `creator.name`. Username comes from
 * `creator.platform.username`, falling back to platform-specific fields
 * (`creator.youtube` / `creator.instagram`) or `creator.handle`. Profile
 * picture comes from `creator.platform.profile_picture_url`, falling back to
 * `creator.profile_pic`.
 */
export function resolveCreatorPlatformProfile(
  creator: PlatformCreatorSource | null | undefined,
  campaignPlatform: CampaignPlatform,
): {
  displayName: string;
  username: string;
  profileUrl: string;
  profilePicture: string;
  isVerified: boolean;
} {
  const plat = String(campaignPlatform ?? '').toLowerCase();

  // 1. Username candidates, in order of preference.
  const platformObj = (creator?.platform as Record<string, unknown> | undefined) ?? {};
  // Legacy creator objects carry the handle under the platform name as a field (e.g. creator.youtube).
  const platformField = (creator as Record<string, unknown> | null | undefined)?.[plat] as
    | string
    | undefined;
  const rawUsername =
    (platformObj.username as string | undefined) ??
    platformField ??
    (creator?.handle as string | undefined) ??
    '';

  // Normalize: drop the leading `@`, trim. Also drop the protocol+host if
  // the field came in as a full URL (e.g. `https://www.youtube.com/@foo`)
  // so the visible `@username` stays readable.
  let username = String(rawUsername ?? '').trim();
  // Strip protocol + domain when the field is a URL — pull the trailing path segment.
  const urlMatch = username.match(/^https?:\/\/[^/]+\/(?:@)?([^/?#]+)/i);
  if (urlMatch && urlMatch[1]) username = urlMatch[1];
  username = username.replace(/^@/, '');

  // 2. Profile URL.
  let profileUrl = '';
  if (typeof platformField === 'string' && /^https?:\/\//i.test(platformField)) {
    profileUrl = platformField;
  } else if (username) {
    profileUrl = PLATFORM_PROFILE_URL[plat]?.(username) ?? '';
  }

  // 3. Display name (channel / profile name, NOT the platform label).
  // Roster entries from the BE wrap creator info under `entry.creator.name`
  // — that's the documented brand-safe shape; check it before falling back
  // to a top-level `name` field that's only present on legacy / synthetic
  // shapes.
  const creatorSub = (creator?.creator as Record<string, unknown> | undefined) ?? {};
  const rawDisplayName =
    (platformObj.name as string | undefined) ??
    (creator?.name as string | undefined) ??
    (creatorSub.name as string | undefined) ??
    '';
  const displayName = String(rawDisplayName ?? '').trim();

  // 4. Profile picture (avatar) — platform-embedded first, then top-level.
  const rawProfilePicture =
    (platformObj.profile_picture_url as string | undefined) ??
    (creator?.profile_pic as string | undefined) ??
    '';
  const profilePicture = String(rawProfilePicture ?? '').trim();

  // 5. Verified badge — BE sets this only for verified Instagram accounts.
  const isVerified = platformObj.is_verified === true;

  return { displayName, username, profileUrl, profilePicture, isVerified };
}

/**
 * Three-element cell: small round avatar on the left, creator's platform
 * display name on top (semibold), `@username` link below. Used by every
 * creators list in the campaign-detail tabs.
 *
 * The link and avatar `e.stopPropagation()` on click so embedding tables
 * with row-level click handlers (open review panel, etc.) don't fire when
 * the user just wanted to open the profile in a new tab.
 *
 * `linkHandle` (default true) renders `@username` as a profile link. Pass
 * `false` where the whole cell is itself a button (e.g. the Creators roster,
 * where the cell opens the analysis drawer) — a nested `<a>` inside a
 * `<button>` is invalid HTML, so the handle degrades to plain text there.
 *
 * When the display name is truncated, hovering ANYWHERE on the cell (avatar,
 * name, handle — not just the name text itself) opens a tooltip with the
 * full name. That matters because every caller nests this whole cell inside
 * one big clickable button/link (open the analysis drawer, open the profile,
 * etc.), so a trigger scoped to just the name span would miss most of the
 * area the user actually hovers.
 */
export function PlatformCreatorCell({
  creator,
  campaignPlatform,
  linkHandle = true,
  subtitle,
}: {
  creator: PlatformCreatorSource | null | undefined;
  campaignPlatform: CampaignPlatform;
  linkHandle?: boolean;
  /** Optional line under the handle (e.g. the admin's PRIVATE / NOT FOUND
   *  badge). Must be non-interactive — callers nest the cell in a button. */
  subtitle?: React.ReactNode;
}): React.ReactElement {
  const { displayName, username, profileUrl, profilePicture, isVerified } =
    resolveCreatorPlatformProfile(creator, campaignPlatform);
  const [nameRef, isTruncated] = useIsTruncated<HTMLSpanElement>(displayName);
  const [tooltipOpen, setTooltipOpen] = React.useState(false);

  // Avatar — falls back to a slate placeholder showing the first letter.
  const initial =
    (displayName || username || '?').trim().charAt(0).toUpperCase() || '?';
  const [imgFailed, setImgFailed] = React.useState(false);
  const avatarNode = profilePicture && !imgFailed
    ? React.createElement('img', {
        src: profilePicture,
        alt: '',
        loading: 'lazy',
        referrerPolicy: 'no-referrer',
        className:
          'h-9 w-9 shrink-0 rounded-full border border-border bg-muted object-cover',
        onError: () => setImgFailed(true),
      })
    : React.createElement(
        'div',
        {
          className:
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border bg-muted text-caption-medium text-muted-foreground',
        },
        initial,
      );

  const handleNode = !username
    ? React.createElement(
        'span',
        { className: 'text-caption-regular text-muted-foreground' },
        '—',
      )
    : profileUrl && linkHandle
      ? React.createElement(
          'a',
          {
            href: profileUrl,
            target: '_blank',
            rel: 'noreferrer',
            onClick: (e: React.MouseEvent) => e.stopPropagation(),
            className:
              'text-caption-regular text-[var(--brand-400)] hover:text-[var(--brand-500)] hover:underline',
          },
          `@${username}`,
        )
      : React.createElement(
          'span',
          { className: 'text-caption-regular text-muted-foreground' },
          `@${username}`,
        );

  // Instagram verified badge — blue circle with a white checkmark.
  const verifiedBadge = isVerified
    ? React.createElement(
        'svg',
        {
          viewBox: '0 0 16 16',
          className: 'ml-1 inline-block h-3.5 w-3.5 flex-shrink-0 text-blue-600',
          fill: 'currentColor',
          'aria-label': 'Verified',
        },
        React.createElement('circle', { cx: 8, cy: 8, r: 8 }),
        React.createElement('path', {
          d: 'M11.5 5.5l-4 4-1.5-1.5',
          stroke: 'white',
          strokeWidth: 1.5,
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
          fill: 'none',
        }),
      )
    : null;

  // Text column — display name on top (when present) + handle link below.
  const nameNode = displayName
    ? React.createElement(
        'span',
        { className: 'flex min-w-0 items-center' },
        React.createElement(
          'span',
          { ref: nameRef, className: 'truncate text-body-sm-semibold text-foreground' },
          displayName,
        ),
        verifiedBadge,
      )
    : null;

  const textCol = nameNode
    ? React.createElement(
        'div',
        { className: 'flex min-w-0 flex-col gap-0.5' },
        nameNode,
        handleNode,
        subtitle,
      )
    : React.createElement(
        'div',
        { className: 'flex min-w-0 flex-col gap-0.5' },
        React.createElement(
          'span',
          { className: 'flex min-w-0 items-center' },
          handleNode,
          verifiedBadge,
        ),
        subtitle,
      );

  const cell = React.createElement(
    'div',
    { className: 'flex min-w-0 items-center gap-3' },
    avatarNode,
    textCol,
  );

  if (!displayName) return cell;

  // Tree shape stays constant regardless of `isTruncated` (only the `open`
  // gate changes) — see useIsTruncated's note on why a conditionally-mounted
  // wrapper can fight its own measurement.
  return React.createElement(
    Tooltip,
    { open: isTruncated && tooltipOpen, onOpenChange: setTooltipOpen },
    React.createElement(TooltipTrigger, { asChild: true }, cell),
    isTruncated ? React.createElement(TooltipContent, { side: 'top' }, displayName) : null,
  );
}
